/**
 * Self-service published-dashboard data pipeline.
 *
 * Each widget produced by the self-service builder carries its raw dataset
 * rows plus aggregation parameters in a `selfService` payload on the widget
 * config. At render/refilter time, ConfigService.getConfig delegates to this
 * utility, which:
 *   1. filters the raw rows by the active global / cross-filter selections,
 *   2. re-aggregates by the configured categoryKey/valueKey/aggregation,
 *   3. reshapes the result to match each chart component's expected input
 *      (bar/line keyed rows, literal {name, value} for pie, {bar: rows} for donut).
 *
 * This is the path that makes global filter dropdowns AND chart-click
 * cross-filters actually re-narrow the charts in the published view — both
 * flows feed through ChartWrapperService → onReload(filters) → getConfig().
 */

export type SelfServiceAggregation =
  | 'sum' | 'avg' | 'count' | 'distinctCount'
  | 'min' | 'max' | 'median' | 'range'
  | 'first' | 'last' | string;

export type SelfServiceDateGranularity =
  'none' | 'day' | 'week' | 'month' | 'quarter' | 'year';

export type SelfServiceSortDirection = 'asc' | 'desc';
export type SelfServiceTopNDirection = 'top' | 'bottom';

export interface SelfServiceWidgetMeta {
  rawRows: Array<Record<string, unknown>>;
  columns?: Array<{ name: string; type?: string }>;
  chartType: string;
  categoryKey: string;
  valueKey: string;
  aggregation?: SelfServiceAggregation;
  sortBy?: string;
  sortDir?: SelfServiceSortDirection;
  topN?: number | null;
  topNDirection?: SelfServiceTopNDirection;
  dateGranularity?: SelfServiceDateGranularity;
  /** Optional: widgetId of the chart this metadata belongs to. Used to skip
   *  self-filtering when a chart click publishes a filter (the source chart
   *  shouldn't filter itself by its own click). */
  selfWidgetId?: string;
}

export interface SelfServiceFilters {
  requestParams?: Record<string, unknown>;
  queryParams?: Record<string, unknown>;
  /** widgetId of the chart that triggered the most recent filter change.
   *  When equal to selfWidgetId on the meta, the corresponding key is skipped. */
  sourceWidgetId?: string | null;
}

const RAW_ROW_CHART_TYPES = new Set<string>([
  'data-table',
  'scatter-plot',
  'bubble-chart',
  'quad-bubble-chart',
  'configurable-multi-series-scatter-chart',
  'kpi-card',
  'treemap-chart',
]);

export function computeSelfServiceData(
  meta: SelfServiceWidgetMeta,
  filters?: SelfServiceFilters,
): unknown {
  const filteredRows = applyFilters(meta.rawRows, meta.columns, filters, meta);

  if (RAW_ROW_CHART_TYPES.has(meta.chartType)) {
    return filteredRows.map((row) => ({ ...row }));
  }

  if (!meta.categoryKey || !meta.valueKey) {
    return filteredRows.map((row) => ({ ...row }));
  }

  const aggregated = aggregateByCategory(filteredRows, meta);

  if (meta.chartType === 'pie-chart') {
    return aggregated.map(({ name, value }) => ({ name, value }));
  }

  const keyedRows = aggregated.map(({ name, value }) => ({
    [meta.categoryKey]: name,
    [meta.valueKey]: value,
  }));

  if (meta.chartType === 'donut-chart') {
    return { bar: keyedRows };
  }

  return keyedRows;
}

function applyFilters(
  rows: Array<Record<string, unknown>>,
  columns: Array<{ name: string; type?: string }> | undefined,
  filters: SelfServiceFilters | undefined,
  meta: SelfServiceWidgetMeta,
): Array<Record<string, unknown>> {
  if (!filters) return rows;

  const columnNames = columns?.length
    ? new Set(columns.map((c) => c.name))
    : null;
  const filterEntries: Array<[string, Set<string>]> = [];

  const collect = (params: Record<string, unknown> | undefined): void => {
    if (!params) return;
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined || value === '') continue;
      if (columnNames && !columnNames.has(key)) continue;
      const values = (Array.isArray(value) ? value : [value])
        .map((v) => String(v ?? '').trim())
        .filter((v) => v !== '');
      if (!values.length) continue;
      filterEntries.push([key, new Set(values)]);
    }
  };
  collect(filters.requestParams);
  collect(filters.queryParams);

  if (!filterEntries.length) return rows;

  return rows.filter((row) =>
    filterEntries.every(([key, valueSet]) => valueSet.has(String(row[key] ?? ''))),
  );
}

function aggregateByCategory(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): Array<{ name: string; value: number }> {
  const datasetRows = applyDateGranularity(rows, meta.categoryKey, meta.dateGranularity);

  const grouped = new Map<string, number[]>();
  datasetRows.forEach((row) => {
    const raw = row[meta.categoryKey];
    const category = raw === undefined || raw === null || String(raw).trim() === ''
      ? '(blank)'
      : String(raw);
    const numbers = grouped.get(category) || [];
    numbers.push(toNumber(row[meta.valueKey]));
    grouped.set(category, numbers);
  });

  const items = Array.from(grouped.entries()).map(([name, values]) => ({
    name,
    value: aggregateValues(values, meta.aggregation || 'sum'),
  }));

  return applyShape(items, meta);
}

function applyShape(
  items: Array<{ name: string; value: number }>,
  meta: SelfServiceWidgetMeta,
): Array<{ name: string; value: number }> {
  let result = items;
  if (meta.sortBy) {
    const dir = meta.sortDir === 'desc' ? -1 : 1;
    result = [...result].sort((a, b) => {
      const useValue = meta.sortBy !== 'name';
      return useValue
        ? (a.value - b.value) * dir
        : a.name.localeCompare(b.name) * dir;
    });
  }
  if (meta.topN && meta.topN > 0) {
    const sortedForLimit = meta.sortBy
      ? result
      : [...result].sort((a, b) => b.value - a.value);
    result = meta.topNDirection === 'bottom'
      ? sortedForLimit.slice(-meta.topN)
      : sortedForLimit.slice(0, meta.topN);
  }
  return result;
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const s = String(value ?? '').replace(/,/g, '').trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function aggregateValues(values: number[], aggregation: SelfServiceAggregation): number {
  if (aggregation === 'count') return values.length;
  if (aggregation === 'distinctCount') return new Set(values).size;
  if (!values.length) return 0;
  if (aggregation === 'avg') return values.reduce((a, b) => a + b, 0) / values.length;
  if (aggregation === 'min') return Math.min(...values);
  if (aggregation === 'max') return Math.max(...values);
  if (aggregation === 'range') return Math.max(...values) - Math.min(...values);
  if (aggregation === 'first') return values[0];
  if (aggregation === 'last') return values[values.length - 1];
  if (aggregation === 'median') {
    const sorted = [...values].sort((a, b) => a - b);
    const rank = 0.5 * (sorted.length - 1);
    const lo = Math.floor(rank);
    const hi = Math.ceil(rank);
    return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (rank - lo);
  }
  // Default + 'sum' + any unsupported custom aggregation key (e.g. 'custom:foo')
  // fall back to sum so the chart still renders something coherent.
  return values.reduce((a, b) => a + b, 0);
}

function applyDateGranularity(
  rows: Array<Record<string, unknown>>,
  xKey: string,
  granularity: SelfServiceDateGranularity | undefined,
): Array<Record<string, unknown>> {
  if (!granularity || granularity === 'none' || !xKey) return rows;
  const bucket = (raw: unknown): string => {
    const d = new Date(String(raw ?? ''));
    if (Number.isNaN(d.getTime())) return String(raw ?? '');
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    switch (granularity) {
      case 'year': return String(y);
      case 'quarter': return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
      case 'month': return `${y}-${String(m).padStart(2, '0')}`;
      case 'week': {
        const start = new Date(d);
        start.setDate(d.getDate() - d.getDay());
        return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
      }
      case 'day':
      default: return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  };
  return rows.map((row) => ({ ...row, [xKey]: bucket(row[xKey]) }));
}
